import { ApiError, type StatementLine } from "@khazzin/api-client";
import { formatDate, formatDateTime } from "@khazzin/i18n/dates";
import { formatMoney } from "@khazzin/i18n/money";
import {
	EmptyState,
	ErrorState,
	KeyValueList,
	NotFoundState,
	PageHeader,
} from "@khazzin/ui/patterns/page";
import { StatusChip } from "@khazzin/ui/patterns/StatusChip";
import { type Column, DataTable } from "@khazzin/ui/patterns/table";
import { Button, Drawer, SkeletonText } from "@khazzin/ui/primitives";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useParams } from "react-router";
import { useApi } from "../../api";
import { useLocale } from "../../app/shell/locale";
import { NotFound } from "../../app/shell/Shell";
import { useStoreScope } from "../../app/shell/scope";
import { levelOf } from "../../app/shell/sections";
import { DisputeDialog } from "./DisputeDialog";
import { financeKey, money, StatementStatusChip, useDownload } from "./FinancePage";

/**
 * One statement (EP10-S08, DA-10-09, `10/02` §5 and §8): its totals, the line
 * that reconciles them — opening + entries = closing — and every line, each
 * opening its ledger entry in a drawer. The open period is Provisional and
 * has no PDF. At `finance = write` an issued line offers Dispute (EP10-S09);
 * a disputed line shows the Disputed chip, linked to its ticket.
 */
export function StatementPage() {
	const { t, locale } = useLocale();
	const { store, context } = useStoreScope();
	const api = useApi();
	const { statementId = "" } = useParams();
	const [entryId, setEntryId] = useState<string | null>(null);
	const [disputing, setDisputing] = useState<StatementLine | null>(null);
	const { failed, busy, download } = useDownload(t);
	const storeId = store?.storeId ?? "";
	const level = levelOf(store, "finance");
	const timeZone = context?.preferences?.timezone ?? "Asia/Beirut";
	const detail = useQuery({
		queryKey: [...financeKey(storeId), "statement", statementId],
		queryFn: ({ signal }) => api.getPortalFinanceStatement(statementId, { signal }),
		enabled: storeId !== "" && level !== "none",
		retry: false,
	});

	if (store !== null && level === "none") {
		return <NotFound />;
	}
	if (detail.isLoading) {
		return <SkeletonText lines={6} />;
	}
	if (detail.error instanceof ApiError && detail.error.status === 404) {
		return <NotFoundState t={t} />;
	}
	if (detail.isError || detail.data === undefined) {
		return (
			<ErrorState
				error={detail.error}
				onRetry={() => void detail.refetch()}
				t={t}
				title={t("portal.finance.statement.failed")}
			/>
		);
	}

	const { statement: st, lines } = detail.data;
	const currency = st.currency;
	const day = (value: string | undefined) =>
		value ? formatDate(value, { locale, timeZone }) : "…";
	const entries = lines.reduce((sum, l) => sum + l.amountMinor, 0);
	// Only an issued statement's line can be disputed: the open period's
	// entries are on no document yet.
	const canDispute = level === "write" && st.status !== "provisional";
	const columns: Column<StatementLine>[] = [
		{
			id: "date",
			headerKey: "portal.finance.lines.date",
			cell: (l) => formatDate(l.occurredAt, { locale, timeZone }),
		},
		{
			id: "category",
			headerKey: "portal.finance.lines.category",
			cell: (l) => t(`portal.finance.category.${l.subLabel ?? l.category}`),
		},
		{
			id: "description",
			headerKey: "portal.finance.lines.description",
			card: "wide",
			cell: (l) => l.description,
		},
		{
			id: "amount",
			headerKey: "portal.finance.lines.amount",
			align: "end",
			cell: (l) => formatMoney({ amountMinor: l.amountMinor, currency }, locale),
		},
		{
			id: "dispute",
			headerKey: "portal.finance.lines.dispute",
			cell: (l) =>
				l.disputed ? (
					l.disputeTicketId ? (
						<Link
							to={`/tickets/${encodeURIComponent(l.disputeTicketId)}`}
							onClick={(event) => event.stopPropagation()}
						>
							<StatusChip tone="warning">{t("portal.finance.lines.disputed")}</StatusChip>
						</Link>
					) : (
						<StatusChip tone="warning">{t("portal.finance.lines.disputed")}</StatusChip>
					)
				) : canDispute ? (
					<Button
						size="sm"
						variant="secondary"
						aria-label={t("portal.finance.dispute.of", { description: l.description })}
						onClick={(event) => {
							event.stopPropagation();
							setDisputing(l);
						}}
					>
						{t("portal.finance.dispute.action")}
					</Button>
				) : (
					"—"
				),
		},
		{
			id: "entry",
			headerKey: "portal.finance.lines.entry",
			cell: (l) => (
				<Button
					size="sm"
					variant="ghost"
					aria-label={t("portal.finance.lines.openEntry", { description: l.description })}
					onClick={() => setEntryId(l.ledgerEntryId)}
				>
					{t("portal.finance.lines.view")}
				</Button>
			),
		},
	];

	return (
		<div className="flex flex-col gap-4">
			<PageHeader
				title={t("portal.finance.statement.title", {
					from: day(st.periodStart),
					to: day(st.periodEnd),
				})}
				backTo="/finance?tab=statements"
				backLabel={t("portal.finance.tabs.statements")}
				chip={<StatementStatusChip statement={st} t={t} />}
				{...(st.hasDocument
					? {
							primaryAction: (
								<Button
									variant="secondary"
									loading={busy === st.statementId}
									onClick={() =>
										void download(st.statementId, () =>
											api.downloadPortalStatementDocument(st.statementId),
										)
									}
								>
									{t("portal.finance.pdf")}
								</Button>
							),
						}
					: {})}
			/>
			{failed !== null && (
				<p role="alert" className="text-[12.5px] text-danger-fg">
					{failed}
				</p>
			)}
			{st.status === "provisional" && (
				<p role="note" className="text-[12.5px] text-fg-muted">
					{t("portal.finance.statement.provisionalNote")}
				</p>
			)}
			<KeyValueList
				rows={[
					{ label: t("portal.finance.statement.opening"), value: money(st.openingBalance, locale) },
					{ label: t("portal.finance.statement.cod"), value: money(st.codCollected, locale) },
					{ label: t("portal.finance.statement.service"), value: money(st.serviceCharges, locale) },
					{ label: t("portal.finance.statement.delivery"), value: money(st.deliveryFees, locale) },
					{
						label: t("portal.finance.statement.adjustments"),
						value: money(st.adjustments, locale),
					},
					{ label: t("portal.finance.statement.tax"), value: money(st.tax, locale) },
					{ label: t("portal.finance.statement.closing"), value: money(st.closingBalance, locale) },
				]}
			/>
			<p className="text-[13px] font-bold text-fg [font-variant-numeric:tabular-nums]">
				{t("portal.finance.statement.reconcile", {
					opening: money(st.openingBalance, locale),
					entries: formatMoney({ amountMinor: entries, currency }, locale),
					closing: money(st.closingBalance, locale),
				})}
			</p>
			<DataTable<StatementLine>
				screenKey="portal.finance.lines"
				caption={t("portal.finance.lines.caption")}
				t={t}
				columns={columns}
				rowKey={(l) => l.lineId}
				page={{ items: lines, nextCursor: null }}
				status="success"
				onRetry={() => void detail.refetch()}
				onRowClick={(l) => setEntryId(l.ledgerEntryId)}
				pagination={{
					showingFrom: lines.length === 0 ? 0 : 1,
					showingTo: lines.length,
					hasNext: false,
					hasPrevious: false,
					onNext: () => {},
					onPrevious: () => {},
				}}
				empty={<EmptyState title={t("portal.finance.lines.empty")} illustration="none" />}
			/>
			<LedgerEntryDrawer
				storeId={storeId}
				entryId={entryId}
				timeZone={timeZone}
				onClose={() => setEntryId(null)}
			/>
			{disputing !== null && <DisputeDialog line={disputing} onClose={() => setDisputing(null)} />}
		</div>
	);
}

function LedgerEntryDrawer({
	storeId,
	entryId,
	timeZone,
	onClose,
}: {
	storeId: string;
	entryId: string | null;
	timeZone: string;
	onClose: () => void;
}) {
	const { t, locale } = useLocale();
	const api = useApi();
	const entry = useQuery({
		queryKey: [...financeKey(storeId), "entry", entryId],
		queryFn: ({ signal }) => api.getPortalLedgerEntry(entryId ?? "", { signal }),
		enabled: entryId !== null,
	});
	const e = entry.data;
	return (
		<Drawer
			open={entryId !== null}
			onOpenChange={(open) => !open && onClose()}
			title={t("portal.finance.entry.title")}
			closeLabel={t("ui.actions.close")}
		>
			{entry.isLoading ? (
				<SkeletonText lines={5} />
			) : entry.isError || e === undefined ? (
				<ErrorState error={entry.error} onRetry={() => void entry.refetch()} t={t} compact />
			) : (
				<KeyValueList
					rows={[
						{ label: t("portal.finance.entry.id"), value: e.entryId, mono: true },
						{ label: t("portal.finance.entry.journal"), value: e.journalId, mono: true },
						{
							label: t("portal.finance.entry.category"),
							value: t(`portal.finance.category.${e.subLabel ?? e.category}`),
						},
						{ label: t("portal.finance.entry.description"), value: e.description },
						{
							label: t("portal.finance.entry.direction"),
							value: t(`portal.finance.entry.${e.direction}`),
						},
						{ label: t("portal.finance.entry.amount"), value: money(e.amount, locale) },
						{
							label: t("portal.finance.entry.posted"),
							value: formatDateTime(e.postedAt, { locale, timeZone }),
						},
						...(e.occurredAt
							? [
									{
										label: t("portal.finance.entry.occurred"),
										value: formatDateTime(e.occurredAt, { locale, timeZone }),
									},
								]
							: []),
						...(e.orderId
							? [{ label: t("portal.finance.entry.order"), value: e.orderId, mono: true }]
							: []),
					]}
				/>
			)}
		</Drawer>
	);
}
